/*
  <sm-carousel> — a roster that drifts sideways once.

  The track is a real scroll container, so touch and trackpad scrolling and
  keyboard arrows all work on their own. This adds two things on top:

    - a slow drift to the right that stops at the last card, paused while a
      pointer is over the row, while something inside it has focus, and while
      the row is off screen;
    - grab-to-scroll for a mouse, since a mouse has no other way to drag a
      scroll container. Touch is left to the browser, which already does it
      better than script can.

  The row shows each story once. It used to clone the cards to wrap for ever,
  which meant the same faces came round again on a second pass.

  Honours prefers-reduced-motion by not drifting; the row still scrolls by hand.
*/
if (!customElements.get('sm-carousel')) {
  customElements.define(
    'sm-carousel',
    class SmCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this.speed = parseFloat(this.dataset.speed);
        if (!Number.isFinite(this.speed)) this.speed = 30;

        this.paused = false;
        this.offScreen = false;
        this.dragging = false;
        this.moved = 0;
        this.limit = 0;

        if (this.hasAttribute('data-shuffle')) this.shuffle();
        this.originals = Array.from(this.track.children);
        this.measure();
        this.bind();

        this.tick = this.tick.bind(this);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reduce = reduce;
        if (!reduce.matches && this.speed > 0) this.frame = requestAnimationFrame(this.tick);

        if (this.dataset.pool) this.watchForPool();
      }

      disconnectedCallback() {
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.resize) this.resize.disconnect();
        if (this.io) this.io.disconnect();
      }

      /* ---------------------------------------------------------------------
         Drawing the row from a bigger roster.

         The cards rendered into the page are the fallback and the thing search
         engines read. If a pool page is set, the row is redrawn from every
         story on that page instead, so it is not the same two dozen faces every
         time. Everything below fails soft: any problem at all and the rendered
         cards simply stay.
         ------------------------------------------------------------------ */

      /* The roster sits below the fold on both pages it is used on, so the
         request waits until the row is nearly in view rather than competing
         with the hero. */
      watchForPool() {
        if (!('IntersectionObserver' in window)) return;
        const io = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            io.disconnect();
            this.loadPool();
          },
          { rootMargin: '300px' }
        );
        io.observe(this);
      }

      async loadPool() {
        const key = `sm-pool-v1:${this.dataset.pool}`;
        let people = null;

        try {
          const cached = sessionStorage.getItem(key);
          if (cached) people = JSON.parse(cached);
        } catch (e) {
          /* private mode, or storage disabled. Not worth caring about. */
        }

        if (!people) {
          try {
            const res = await fetch(this.dataset.pool, { credentials: 'same-origin' });
            if (!res.ok) return;
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            people = SmCarousel.readCards(doc);
            try {
              sessionStorage.setItem(key, JSON.stringify(people));
            } catch (e) {
              /* over quota, or storage disabled. The draw still works. */
            }
          } catch (e) {
            return;
          }
        }

        const want = this.originals.length;
        const chosen = this.constructor.draw(people, this.dataset.poolHalf, want);
        if (chosen.length < want) return; // not enough to fill the row, leave it alone
        this.applyPool(chosen);
      }

      static readCards(doc) {
        return Array.from(doc.querySelectorAll('.highlight-card'))
          .map((card) => {
            const img = card.querySelector('img');
            return {
              href: card.getAttribute('href'),
              name: card.querySelector('.person-card__name')?.textContent.trim(),
              meta: card.querySelector('.person-card__meta')?.textContent.trim() || '',
              src: img?.getAttribute('src'),
              srcset: img?.getAttribute('srcset') || '',
              alt: img?.getAttribute('alt') || '',
            };
          })
          .filter((p) => p.href && p.name && p.src);
      }

      /* The roster is lopsided: far more musicians and founders than fighters.
         A blind draw would show that shape back every time, so the row takes an
         even share from each kind of guest instead. */
      static bucket(meta) {
        const m = (meta || '').toLowerCase();
        const groups = [
          ['fighter', ['fighter', 'bkfc', 'mma', 'boxer', 'bare']],
          ['music', ['songwriter', 'musician', 'artist', 'band', 'singer', 'guitar', 'rock', 'country']],
          ['founder', ['founder', 'ceo', 'owner']],
          ['athlete', ['athlete', 'coach', 'trainer', 'running back', 'quarterback', 'basketball', 'track', 'golf', 'surf']],
          ['voice', ['author', 'speaker', 'podcast', 'media', 'expert', 'consultant', 'veteran', 'chef']],
        ];
        const hit = groups.find(([, keys]) => keys.some((k) => m.includes(k)));
        return hit ? hit[0] : 'other';
      }

      /* Splitting each kind of guest in half by a stable sort, rather than
         hashing the whole roster, keeps the two halves evenly matched inside
         every bucket. Hashing can hand one side nine fighters and the other
         three, and then the short side cannot fill its share. */
      static draw(people, half, want) {
        const buckets = new Map();
        people.forEach((p) => {
          const b = SmCarousel.bucket(p.meta);
          if (!buckets.has(b)) buckets.set(b, []);
          buckets.get(b).push(p);
        });

        const mine = [];
        buckets.forEach((list) => {
          list.sort((a, b) => (a.href < b.href ? -1 : 1));
          const side = list.filter((_, i) => (i % 2 === 0) === (half !== 'b'));
          mine.push(side);
        });

        const shuffle = (arr) => {
          for (let i = arr.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
          }
          return arr;
        };

        const share = Math.floor(want / mine.length);
        const picked = [];
        const spare = [];
        mine.forEach((side) => {
          shuffle(side);
          picked.push(...side.slice(0, share));
          spare.push(...side.slice(share));
        });
        /* A thin bucket leaves the row short, so top it up from whatever is
           left rather than returning fewer cards than the row expects. */
        shuffle(spare);
        while (picked.length < want && spare.length) picked.push(spare.pop());
        return shuffle(picked).slice(0, want);
      }

      applyPool(people) {
        const template = this.originals[0];
        if (!template) return;

        const cards = people.map((p) => {
          const card = template.cloneNode(true);
          card.removeAttribute('aria-hidden');
          card.removeAttribute('tabindex');
          /* The template is a card already in the page, so it may carry the
             effects layer's reveal attribute. Cloning that would hand every
             redrawn card a hidden state with nothing watching to un-hide it. */
          card.removeAttribute('data-fx-reveal');
          card.classList.remove('fx-in');
          if (card.tagName === 'A') card.setAttribute('href', p.href);
          const name = card.querySelector('.person-card__name');
          if (name) name.textContent = p.name;
          const meta = card.querySelector('.person-card__meta');
          if (meta) meta.textContent = p.meta;
          const img = card.querySelector('img');
          if (img) {
            img.setAttribute('src', p.src);
            /* The index sizes its images for a three-across grid. Keeping its
               srcset here would pull a wider file than this row ever shows, so
               the template's own sizes attribute is left in place and only the
               candidates change. */
            if (p.srcset) img.setAttribute('srcset', p.srcset);
            img.setAttribute('alt', p.alt || p.name);
            img.removeAttribute('width');
            img.removeAttribute('height');
          }
          return card;
        });

        if (this.frame) cancelAnimationFrame(this.frame);
        this.track.classList.add('is-swapping');

        const swap = () => {
          this.track.replaceChildren(...cards);
          this.originals = Array.from(this.track.children);
          this.track.scrollLeft = 0;
          // Let the effects layer see the new cards.
          if (window.smFx) window.smFx.refresh();
          this.pos = 0;
          this.measure();
          this.track.classList.remove('is-swapping');
          if (!this.reduce.matches && this.speed > 0) this.frame = requestAnimationFrame(this.tick);
        };

        /* Let the fade out actually render before the cards change under it. */
        if (this.reduce.matches) swap();
        else setTimeout(swap, 220);
      }

      /* Reorder the cards before the row is measured.

         Fisher-Yates over a detached fragment, so the browser lays the row out
         once rather than once per move. */
      shuffle() {
        const cards = Array.from(this.track.children);
        for (let i = cards.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        const frag = document.createDocumentFragment();
        cards.forEach((card) => frag.appendChild(card));
        this.track.appendChild(frag);
      }

      /* How far the row can travel before the last card is flush with the
         right edge. Card widths are set in CSS off the viewport, so this is
         re-measured whenever the track resizes. */
      measure() {
        this.limit = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      bind() {
        const pause = () => { this.paused = true; };
        const resume = () => { if (!this.dragging) this.paused = false; };

        this.addEventListener('pointerenter', pause);
        this.addEventListener('pointerleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        // A finger on the track is the browser scrolling it; stay out of the way.
        this.track.addEventListener('touchstart', pause, { passive: true });
        this.track.addEventListener('touchend', resume, { passive: true });
        this.track.addEventListener('touchcancel', resume, { passive: true });

        /* Images and links are draggable by default, so pressing on a card can
           start the browser's own drag, ghost image and all, instead of
           scrolling the row. Which one wins is a race, so it looks intermittent.
           Nothing here should ever be dragged out of the row. */
        this.track.addEventListener('dragstart', (e) => e.preventDefault());

        this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
        const end = this.onPointerUp.bind(this);
        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', end);
        // Suppress the click that ends a drag, or grabbing the row opens a page.
        this.track.addEventListener('click', (e) => {
          if (this.moved > 5) { e.preventDefault(); e.stopPropagation(); }
        }, true);

        this.resize = new ResizeObserver(() => this.measure());
        this.resize.observe(this.track);

        // Nothing to animate while the section is out of view.
        this.io = new IntersectionObserver(
          ([entry]) => { this.offScreen = !entry.isIntersecting; },
          { rootMargin: '200px' }
        );
        this.io.observe(this);
      }

      onPointerDown(e) {
        if (e.pointerType === 'touch') return; // native scrolling handles it
        this.dragging = true;
        this.captured = false;
        this.paused = true;
        this.moved = 0;
        this.startX = e.clientX;
        this.startScroll = this.track.scrollLeft;
        /* Neither the is-dragging class nor setPointerCapture belongs here.
           The class kills pointer-events on the cards and capture retargets
           the event, so either one applied on pointerdown leaves the click
           landing on the track, where closest('a') finds nothing and the card
           never opens. Both wait until the pointer has actually travelled. */
      }

      onPointerMove(e) {
        if (!this.dragging) return;
        const dx = e.clientX - this.startX;
        this.moved = Math.max(this.moved, Math.abs(dx));
        /* Once this is unmistakably a drag rather than a click, take the
           pointer so the row keeps following it even past the edge of the
           track. A click never gets this far, so its target stays the card. */
        if (!this.captured && this.moved > 5) {
          this.track.setPointerCapture(e.pointerId);
          this.track.classList.add('is-dragging');
          this.captured = true;
        }
        /* The track is a real scroll container, so it clamps at both ends on
           its own. Keep pos in step or the drift would resume from wherever it
           left off and jump the row. */
        this.track.scrollLeft = this.startScroll - dx;
        this.pos = this.track.scrollLeft;
      }

      onPointerUp(e) {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        if (this.captured && this.track.hasPointerCapture && this.track.hasPointerCapture(e.pointerId)) {
          this.track.releasePointerCapture(e.pointerId);
        }
        this.captured = false;
        // Still under the cursor, so stay paused until it leaves.
        this.paused = this.matches(':hover');
      }

      tick(now) {
        if (this.last === undefined) this.last = now;
        // A backgrounded tab hands back one huge delta; cap it so the row does
        // not lurch when you come back to it.
        const dt = Math.min(now - this.last, 100);
        this.last = now;

        if (!this.paused && !this.offScreen && !this.dragging && this.limit > 0) {
          /* The drift is well under a pixel a frame, and scrollLeft reads back
             rounded, so incrementing it directly loses every step and the row
             never moves. Carry the position as a float and write it out. */
          if (this.pos === undefined || Math.abs(this.track.scrollLeft - this.written) > 1) {
            this.pos = this.track.scrollLeft; // something else moved it
          }
          this.pos += (this.speed * dt) / 1000;
          // Park at the last card rather than snapping back to the first.
          if (this.pos >= this.limit) this.pos = this.limit;
          this.track.scrollLeft = this.pos;
          this.written = this.track.scrollLeft;
        }
        this.frame = requestAnimationFrame(this.tick);
      }
    }
  );
}
